import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Div, Loader, Title } from '@gaiads/telia-react-component-library';
import {
  AdminBundle,
  LocaleResponse,
  BundleTranslation,
  VariantOverview,
  AdminProductVariant,
  AdminBundleItem,
  BundleImage,
} from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '../../store';
import { selectAllLocales, selectCurrentLocale } from '../../store/locales';
import { fetchAdminCategories, selectAllCategories } from '../../store/adminCategories';
import { setNotification } from '../../store/ui';
import { BundleEdit } from './BundleEdit/BundleEdit';
import { VariantEverything, isVariantEverything } from './BundleEdit/utils';
import { getInitialAdminBundle, getInitialBundleTranslation } from '../../utils/bundles';
import { productServiceAdminBundlesClient, productServiceAdminProductsClient } from '../../api-client/product-service-client';
import { selectAllSalesChannels } from '../../store/salesChannels';
import { selectAllCountries } from '../../store/adminCountries';

interface BundleFormParamTypes {
  bundleId?: string;
}

export default function BundleForm() {
  const bundleId = Number(useParams<BundleFormParamTypes>().bundleId);

  const { t } = useTranslation('bundlescreen');
  const dispatch = useDispatch();

  const locales = useSelector((state: RootState) => selectAllLocales(state));

  const [addMode, setAddMode] = useState<boolean>(false);
  const [adminProductVariantCache, setAdminProductVariantCache] = useState<AdminProductVariant[]>([]);
  const [currentLocale, setCurrentLocale] = useState<LocaleResponse>(useSelector(selectCurrentLocale) || locales?.[0]);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const allCategoriesInSystem = useSelector((state: RootState) => selectAllCategories(state));
  const allSalesChannelsInSystem = useSelector((state: RootState) => selectAllSalesChannels(state));
  const allCountriesInSystem = useSelector((state: RootState) => selectAllCountries(state));

  // Bundle data split to smaller parts
  const [id, setId] = useState<AdminBundle['id']>();
  const [name, setName] = useState<AdminBundle['name']>();
  const [financeId, setFinanceId] = useState<AdminBundle['finance_id']>();
  const [description, setDescription] = useState<AdminBundle['description']>();
  const [images, setImages] = useState<AdminBundle['images']>();

  const [mainItemOptionIds, setMainItemOptionIds] = useState<AdminBundle['main_item_option_ids']>();
  const [items, setItems] = useState<AdminBundle['items']>();
  const [variants, setVariants] = useState<VariantEverything[]>();

  const [bundlePrices, setBundlePrices] = useState<AdminBundle['prices']>([]);

  // Split translations of bundle into even smaller parts
  // translations metadata maps localeId to translation id
  const [translationsMetadata, setTranslationsMetadata] = useState<{ id: number; localeId: number }[]>();
  const [nameTranslations, setNameTranslations] = useState<{ localeId: number; translation: string }[]>();
  const [descriptionTranslations, setDescriptionTranslations] = useState<{ localeId: number; translation: string }[]>();

  // Create a memoized callback for changing each part of the data
  const handleNameChange = useCallback((name: string) => {
    setName(name);
  }, []);

  const handleFinanceIdChange = useCallback((financeId: string) => {
    setFinanceId(financeId);
  }, []);

  const handleImagesChange = useCallback((images: BundleImage[]) => {
    setImages(images);
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setDescription(description);
  }, []);

  const handleNameTranslationsChange = useCallback(
    (
      translations: {
        localeId: number;
        translation: string;
      }[],
    ) => {
      setNameTranslations(translations);
    },
    [],
  );

  const handleDescriptionTranslationsChange = useCallback(
    (
      translations: {
        localeId: number;
        translation: string;
      }[],
    ) => {
      setDescriptionTranslations(translations);
    },
    [],
  );

  const handleMainItemOptionIdsChange = useCallback((optionIds: number[]) => {
    setMainItemOptionIds(optionIds);
  }, []);

  const handleItemsChange = useCallback((items: AdminBundleItem[]) => {
    setItems(items);
  }, []);

  const handleVariantsChange = useCallback(
    async (variants: (VariantOverview | VariantEverything)[]): Promise<boolean> => {
      let updatedAdminProductVariantCache = [...adminProductVariantCache];
      const updatedVariants: VariantEverything[] = [];
      let error = false;
      for (const variant of variants) {
        if (isVariantEverything(variant)) {
          updatedVariants.push(variant);
        } else {
          // Append to VariantOverview data for example TFIDs and TFAPs from corresponding AdminProductVariant
          try {
            // Each variant should have product id
            if (!variant.product_id) {
              error = true;
              break;
            }
            if (!updatedAdminProductVariantCache.some(adminVariant => adminVariant.id === variant.id)) {
              const adminProductResponse = await productServiceAdminProductsClient.getAdminProduct(variant.product_id);
              updatedAdminProductVariantCache = _.uniqBy(
                [...updatedAdminProductVariantCache, ...adminProductResponse.data.product.variants],
                variant => variant.id,
              );
            }
            updatedVariants.push(
              _.merge(
                variant,
                updatedAdminProductVariantCache.find(adminVariant => adminVariant.id === variant.id),
              ),
            );
          } catch {
            dispatch(
              setNotification({
                text: t('errors.adding_variant_error'),
                show: true,
                type: 'warning',
              }),
            );
            error = true;
            break;
          }
        }
      }
      if (!error) {
        setAdminProductVariantCache(updatedAdminProductVariantCache);
        setVariants(updatedVariants);
      }
      return !error;
    },
    [dispatch, t, adminProductVariantCache],
  );

  const handleLocaleChange = useCallback(
    (localeId: number) => {
      const chosenLocale = locales.find(locale => locale.id === localeId);
      if (chosenLocale) {
        setCurrentLocale(chosenLocale);
      }
    },
    [locales],
  );

  const initializeBundleData = useCallback(
    (bundle: AdminBundle, adminProductVariants: AdminProductVariant[], variantOverviews: VariantOverview[]) => {
      setId(bundle.id);
      setName(bundle.name);
      setFinanceId(bundle.finance_id);
      setDescription(bundle.description);
      setImages(bundle.images);

      setMainItemOptionIds(bundle.main_item_option_ids);
      setItems(bundle.items);
      const variants = adminProductVariants.map(adminProductVariant => {
        const variantOverview = variantOverviews.find(variantOverview => variantOverview.id === adminProductVariant.id);
        return _.merge(adminProductVariant, variantOverview);
      });
      setVariants(variants);
      setAdminProductVariantCache(adminProductVariants);

      setBundlePrices(bundle.prices);

      const initializeTranslation = (
        translation: BundleTranslation,
        translationsMetadata: { id: number; localeId: number }[],
        nameTranslations: { localeId: number; translation: string }[],
        descriptionTranslations: { localeId: number; translation: string }[],
      ) => {
        if (translation.locale_id) {
          translationsMetadata.push({ localeId: translation.locale_id, id: translation.id });
          nameTranslations.push({ localeId: translation.locale_id, translation: translation.name || '' });
          descriptionTranslations.push({ localeId: translation.locale_id, translation: translation.description || '' });
        }
      };

      const translationsMetadata: { id: number; localeId: number }[] = [];
      const nameTranslations: { localeId: number; translation: string }[] = [];
      const descriptionTranslations: { localeId: number; translation: string }[] = [];

      locales.forEach(locale => {
        const translation = bundle.translations?.find(t => t.locale_id === locale.id) || getInitialBundleTranslation(locale.id);
        initializeTranslation(translation, translationsMetadata, nameTranslations, descriptionTranslations);
      });

      setTranslationsMetadata(translationsMetadata);
      setNameTranslations(nameTranslations);
      setDescriptionTranslations(descriptionTranslations);
    },
    [locales],
  );

  useEffect(() => {
    async function fetchBundleAndBundleRelatedData() {
      try {
        await dispatch(fetchAdminCategories());
        if (bundleId) {
          const bundleResponse = await productServiceAdminBundlesClient.getAdminBundle(bundleId);
          const bundleContentOverviewResponse = await productServiceAdminBundlesClient.getAdminBundleContentOverview(bundleId);
          const variantOverviews = _.uniqBy(
            [...bundleContentOverviewResponse.data.main_item_options, ...bundleContentOverviewResponse.data.items.flatMap(item => item.options)],
            variant => variant.id,
          );
          initializeBundleData(bundleResponse.data.bundle, bundleResponse.data.variants || [], variantOverviews);
          setAddMode(false);
        } else {
          initializeBundleData(getInitialAdminBundle(), [], []);
          setAddMode(true);
        }
      } catch {
        dispatch(
          setNotification({
            text: t('errors.bundle_initial_fetch_error'),
            show: true,
            type: 'warning',
          }),
        );
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchBundleAndBundleRelatedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleId]);

  const render = (): JSX.Element => {
    let content;

    if (loading) {
      content = (
        <Div alignTextToCenter padding={{ top: 'xlg' }}>
          <Loader size="lg" />
        </Div>
      );
    } else if (error) {
      content = (
        <Div alignTextToCenter padding={{ top: 'xlg' }}>
          <Title>{t('errors.bundle_initial_fetch_error')}</Title>
        </Div>
      );
    } else if (locales.length === 0 || allCategoriesInSystem.length === 0) {
      content = (
        <Div alignTextToCenter padding={{ top: 'xlg' }}>
          <Title>{t('errors.dependency_data_error')}</Title>
        </Div>
      );
    } else {
      content = (
        <BundleEdit
          id={id}
          name={name}
          handleNameChange={handleNameChange}
          nameTranslations={nameTranslations}
          handleNameTranslationsChange={handleNameTranslationsChange}
          financeId={financeId}
          handleFinanceIdChange={handleFinanceIdChange}
          description={description}
          handleDescriptionChange={handleDescriptionChange}
          descriptionTranslations={descriptionTranslations}
          handleDescriptionTranslationsChange={handleDescriptionTranslationsChange}
          translationsMetadata={translationsMetadata}
          images={images}
          handleImagesChange={handleImagesChange}
          mainItemOptionIds={mainItemOptionIds}
          handleMainItemOptionIdsChange={handleMainItemOptionIdsChange}
          items={items}
          handleItemsChange={handleItemsChange}
          variants={variants}
          handleVariantsChange={handleVariantsChange}
          allCategoriesInSystem={allCategoriesInSystem}
          allSalesChannelsInSystem={allSalesChannelsInSystem}
          allCountriesInSystem={allCountriesInSystem}
          bundlePrices={bundlePrices}
          currentLocale={currentLocale}
          locales={locales}
          handleLocaleChange={handleLocaleChange}
          addMode={addMode}
        />
      );
    }

    return <Div margin={{ vertical: 'lg', horizontal: 'xxlg' }}>{content}</Div>;
  };

  return render();
}
